import React, { FC, ReactNode, useEffect, useState } from 'react';
import { BaseModal, Button, Input, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import { apiService } from 'utils/api';
import { NoticeDataType } from 'pages/kitchen/settings/notice-card';
import { SelectionRangeType } from 'components/base/calendar';
import { toast } from 'react-toastify';

type NoticeBoardProps = {
  show: boolean;
  children?: ReactNode;
  onClose: (refetchData?: boolean) => void;
  type?: 'create' | 'edit';
  notice?: NoticeDataType;
};

interface NoticeBoardFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  disabled: boolean;
  type?: 'create' | 'edit';
}

const NoticeBoardFooter: FC<NoticeBoardFooterProps> = ({ onCancel, onConfirm, type, disabled }) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Save as Draft
    </Button>
    <Button
      disabled={disabled}
      small
      color='primary'
      variant='block'
      className='mx-auto'
      onClick={onConfirm}>
      {type === 'create' ? 'Post Now' : 'Update and Post'}
    </Button>
  </div>
);

type FormType = Pick<NoticeDataType, 'title' | 'description'>;

const NoticeBoardModal = ({ show, onClose, type = 'create', notice }: NoticeBoardProps) => {
  const emptyForm = {
    title: '',
    description: ''
  };

  const [formData, setFormData] = useState<FormType>(emptyForm);
  const [selectionRange] = useState({} as SelectionRangeType);
  // eslint-disable-next-line
  const [data, setData] = useState([] as NoticeDataType[]);

  const updateBoard = async () => {
    if (type === 'create') {
      handleCreate();
    } else {
      handleEdit();
    }
  };

  const handleCreate = async () => {
    try {
      const res = await apiService.post('kitchen/team-notification', {
        baseUrl: 'https://order-api.getfoodcourt.com/v1/',
        data: formData
      });

      if (res) {
        toast(<ToastMessage type='success' message='Notice successful created' />);
        onClose(true);
      }
    } catch (err: any) {
      console.log(err);
    }
  };

  const handleEdit = async () => {
    try {
      const res = await apiService.put(`kitchen/team-notification/${notice?.id}`, {
        baseUrl: 'https://order-api.getfoodcourt.com/v1/',
        data: {
          ...formData,
          status: notice?.status
        }
      });
      if (res) {
        toast(<ToastMessage type='success' message='Notice successful updated' />);
        fetchData();
        onClose(true);
      }
    } catch (err: any) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (show) {
      setFormData(emptyForm);
    }
  }, [show]);

  const fetchData = async () => {
    try {
      const res = await apiService.post('kitchen/get-team-notification', {
        baseUrl: 'https://order-api.getfoodcourt.com/v1/',
        data: { start_date: selectionRange.startDate, end_date: selectionRange.endDate }
      });
      setData(res.data.data);
    } catch (err: any) {
      console.log(err);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const updateNoticeBoard = () => {
    if (!notice) return;
    if (type === 'edit') {
      setFormData(notice);
    }
  };

  useEffect(() => {
    updateNoticeBoard();
  }, [notice]);

  const validator = !formData.title || !formData.description;

  return (
    <BaseModal
      bodyOpenClassName='hide-modal-icon'
      titleIcon={<AddIcon className='text-blue-5' />}
      isOpen={show}
      title=''
      titleIconColor='info'
      onRequestClose={() => {
        onClose();
        if (type === 'create') {
          setFormData(emptyForm);
        }
      }}
      footer={
        <NoticeBoardFooter
          disabled={validator}
          type={type}
          onCancel={onClose}
          onConfirm={updateBoard}
        />
      }>
      <div className='flex flex-col gap-5 pb-5'>
        <div className='absolute left-44 top-8'>
          <div className='flex gap-[7px] items-center'>
            <p className='font-semibold text-[#1A1A1A] text-[20px]'>Notice Board</p>
          </div>
        </div>

        <div>
          <p className='font-semibold'>Title</p>
          <Input
            name='title'
            small
            inputClass='placeholder:text-sm'
            className='text-[14px] '
            placeholder='Title'
            value={formData?.title}
            onChange={handleChange}
          />
        </div>

        <div>
          <p className='font-semibold'>Add a note</p>
          <Textarea
            name='description'
            value={formData?.description}
            placeholder='Type your message...'
            className='w-full h-36'
            onChange={handleChange}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default NoticeBoardModal;
